/**
 * Parses a goal review comment into its constituent parts.
 * The comment is now expected to be in the format `<expectedValue>|<sivaComment>`.
 * This format allows for the inclusion of '|' characters within the `sivaComment`.
 * If the input is not in this format, or if the `expectedValue` is not a number,
 * the function defaults to returning an object with an empty `sivaComment` and the `fallbackExpectedValue` if provided, otherwise 0.
 *
 * @param {string | undefined} comment - The goal review comment to be parsed. Should be in the format `<expectedValue>|<sivaComment>` or undefined.
 * @param {number | undefined} [fallbackExpectedValue] - An optional fallback value for `expectedValue` if the parsed value is not a number.
 * @returns {{ sivaComment: string; expectedValue: number }} An object containing `sivaComment` and `expectedValue`. Returns the `fallbackExpectedValue` or 0 for `expectedValue` if the input is invalid or not in the expected format.
 */
export function parseGoalReviewComment(
  comment: string | undefined,
  fallbackExpectedValue?: number,
): {
  sivaComment: string;
  expectedValue: number;
} {
  const parts = comment?.split('|') ?? [];

  if (parts.length < 2) {
    return {
      sivaComment: '',
      expectedValue: fallbackExpectedValue ?? 0,
    };
  }

  const expectedValue = Number(parts[0]);
  if (isNaN(expectedValue)) {
    return {
      sivaComment: '',
      expectedValue: fallbackExpectedValue ?? 0,
    };
  }

  const sivaComment = parts.slice(1).join('|');

  return {
    sivaComment,
    expectedValue,
  };
}

/**
 * Formats a goal review comment by combining `expectedValue` and `sivaComment`.
 * The output is a string in the format `<expectedValue>|<sivaComment>`, suitable for parsing by `parseGoalReviewComment`.
 * This format change accommodates the possibility of including '|' characters in `sivaComment`.
 *
 * @param {{ sivaComment: string; expectedValue: number }} param0 - An object containing `sivaComment` and `expectedValue`.
 * @returns {string} A formatted goal review comment in the format `<expectedValue>|<sivaComment>`.
 */
export function formatGoalReviewComment({
  sivaComment,
  expectedValue,
}: {
  sivaComment: string;
  expectedValue: number;
}): string {
  return `${expectedValue}|${sivaComment}`;
}
